import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, DollarSign, TrendingUp, QrCode, Moon, ShoppingCart,
  Loader2, RefreshCw, CreditCard, Wallet, Building2, Package,
  ArrowUpRight, SplitSquareHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function StatCard({ icon: Icon, label, value, sub, color = 'text-purple-400' }) {
  return (
    <Card className="bg-white/[0.03] border-white/5">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className={`w-3.5 h-3.5 ${color}`} />
          <span className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-lg font-bold heading-font text-white">{value}</p>
        {sub && <p className="text-[9px] text-gray-600 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function PurchaseRow({ purchase }) {
  const statusColors = {
    completed: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5',
    pending: 'text-amber-400 border-amber-500/20 bg-amber-500/5',
    created: 'text-blue-400 border-blue-500/20 bg-blue-500/5',
  };
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.03] last:border-0" data-testid={`purchase-${purchase.purchase_id}`}>
      <div className="w-8 h-8 rounded-lg bg-[#F7931A]/10 flex items-center justify-center shrink-0">
        {purchase.payment_method === 'crypto'
          ? <Wallet className="w-3.5 h-3.5 text-[#F7931A]" />
          : <CreditCard className="w-3.5 h-3.5 text-[#F7931A]" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs text-white font-medium truncate">{purchase.product_name}</p>
          <Badge variant="outline" className={`text-[7px] h-3.5 shrink-0 ${statusColors[purchase.payment_status] || statusColors.created}`}>
            {purchase.payment_status}
          </Badge>
        </div>
        <p className="text-[9px] text-gray-500">{purchase.vendor_name} | {purchase.guest_name || 'Guest'}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-white">${purchase.sale_price}</p>
        <div className="flex items-center gap-1 justify-end">
          <span className="text-[8px] text-emerald-400">+${purchase.split?.getbitsy_share}</span>
          <span className="text-[8px] text-gray-600">|</span>
          <span className="text-[8px] text-blue-400">+${purchase.split?.hotel_share}</span>
        </div>
      </div>
    </div>
  );
}

function SplitVisualizer({ commission_config }) {
  if (!commission_config) return null;
  const afPct = commission_config.affiliate_pct;
  const splitPct = commission_config.getbitsy_hotel_split;
  const vendorPct = 100 - afPct;
  const gbPct = afPct * (splitPct / 100);
  const htPct = afPct - gbPct;

  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid="split-visualizer">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <SplitSquareHorizontal className="w-4 h-4 text-[#F7931A]" />
          <h3 className="text-sm font-semibold heading-font text-gray-200">Auto-Split Model</h3>
        </div>
        <div className="flex items-center gap-1 h-8 rounded-lg overflow-hidden mb-3">
          <div style={{ width: `${vendorPct}%` }} className="h-full bg-purple-600/60 flex items-center justify-center">
            <span className="text-[9px] text-white font-medium">{vendorPct}%</span>
          </div>
          <div style={{ width: `${gbPct}%` }} className="h-full bg-[#F7931A]/60 flex items-center justify-center">
            <span className="text-[9px] text-white font-medium">{gbPct}%</span>
          </div>
          <div style={{ width: `${htPct}%` }} className="h-full bg-blue-500/60 flex items-center justify-center">
            <span className="text-[9px] text-white font-medium">{htPct}%</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-500" /><span className="text-[9px] text-gray-400">Vendor ({vendorPct}%)</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#F7931A]" /><span className="text-[9px] text-gray-400">getbitsy ({gbPct}%)</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-[9px] text-gray-400">Hotel ({htPct}%)</span></div>
        </div>
        <p className="text-[9px] text-gray-600 mt-2 text-center">
          Fiat via Stripe Connect | Crypto via 0xSplits | All recorded on Room SBT
        </p>
      </CardContent>
    </Card>
  );
}

export default function VendorRevenuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/vendor/payments/revenue`);
      setData(await res.json());
    } catch { toast.error('Failed to load revenue data'); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  const p = data?.purchases || {};
  const n = data?.nightly || {};
  const q = data?.qr_codes || {};

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="vendor-revenue-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/vendors" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F7931A]/20 to-emerald-500/20 border border-[#F7931A]/20 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-[#F7931A]" />
              </div>
              <div>
                <h1 className="text-base font-bold heading-font">Revenue Dashboard</h1>
                <p className="text-[10px] text-gray-500">Guest Purchases & Per-Night Tracking</p>
              </div>
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            className="text-xs gap-1.5 h-8 border-white/10 text-gray-400 hover:bg-white/5"
            onClick={() => fetchData(true)} disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={ShoppingCart} label="Sales" value={p.total || 0} sub={`${p.completed || 0} completed`} color="text-[#F7931A]" />
          <StatCard icon={DollarSign} label="Revenue" value={`$${(p.total_sales || 0).toLocaleString()}`} color="text-white" />
          <StatCard icon={ArrowUpRight} label="getbitsy" value={`$${(p.getbitsy_earned || 0).toLocaleString()}`} color="text-emerald-400" />
          <StatCard icon={Building2} label="Hotel Earned" value={`$${(p.hotel_earned || 0).toLocaleString()}`} color="text-blue-400" />
          <StatCard icon={Moon} label="Nights" value={n.total_nights_tracked || 0} sub={`$${(n.total_nightly_revenue || 0).toLocaleString()} revenue`} color="text-purple-400" />
          <StatCard icon={QrCode} label="QR Scans" value={q.total_scans || 0} sub={`${q.conversion_rate || 0}% conversion`} color="text-cyan-400" />
        </div>

        {/* Split Visualizer */}
        <SplitVisualizer commission_config={data?.commission_config} />

        {/* Per-Night Rates */}
        {data?.per_night_rates && (
          <Card className="bg-white/[0.03] border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Moon className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-semibold heading-font text-gray-200">Per-Night Rates</h3>
                <Badge variant="outline" className="text-[8px] h-4 border-white/10 text-gray-500">Hotel pays getbitsy per occupied night</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.per_night_rates).map(([cat, rate]) => (
                  <div key={cat} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/[0.02] rounded-lg border border-white/5">
                    <Package className="w-3 h-3 text-gray-500" />
                    <span className="text-[10px] text-gray-400">{cat}</span>
                    <span className="text-[10px] font-bold text-[#F7931A]">${rate}/night</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Purchases */}
        <Card className="bg-white/[0.03] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-4 h-4 text-[#F7931A]" />
              <h3 className="text-sm font-semibold heading-font text-gray-200">Recent Purchases</h3>
            </div>
            {data?.recent_purchases?.length > 0 ? (
              <div>
                {data.recent_purchases.map(purchase => (
                  <PurchaseRow key={purchase.purchase_id} purchase={purchase} />
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-gray-600 text-center py-6">No purchases yet. Guests will discover products via room QR codes.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
